import type { IMessage, MessageType, BatchPayload } from "@shared/types";
import {
  showReconnectingOverlay,
  hideReconnectingOverlay,
  latencyStats,
  updateLatencyDisplay,
} from "./ui";
import { getLocalPlayerId } from "./gameState";
import { calculateBackoffDelay } from "./utils";
import * as messageHandlers from "./messageHandlers";

// Function to determine the WebSocket URL based on environment
function getWebSocketUrl() {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";

  // If we're on localhost or 127.0.0.1, connect to the dev server port
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    // Use port 8080 for local development
    return `${protocol}//${hostname}:8080/ws`;
  }

  // In production, connect to the same host (using default ports 443/80)
  return `${protocol}//${hostname}/ws`;
}

export const serverUrl = getWebSocketUrl();
export let socket: WebSocket | null = null;

// Connection state variables
let reconnectAttempt = 0;
const MAX_RECONNECT_ATTEMPTS = 10;
const BASE_RECONNECT_DELAY = 1000; // 1 second
let reconnectTimeout: number | null = null;
let heartbeatInterval: number | null = null;
let disconnectedSince: number | null = null;
let manuallyDisconnected = false;
let cleanupComplete = false;

// Implement message batching to reduce network overhead
// Track pending messages for batching
const pendingMessages: IMessage[] = [];
let batchSendInterval: number | null = null;
const MIN_BATCH_INTERVAL = 8; // ms - faster for responsive inputs
const MAX_BATCH_INTERVAL = 32; // ms - more efficient for low activity
let currentBatchInterval = 16; // Start with default timing
let lastActivity = 0;

// Setup page unload handlers to properly disconnect
function setupPageUnloadHandlers(): void {
  window.addEventListener("beforeunload", () => {
    console.log("Page is being unloaded, cleaning up connection");
    cleanupConnectionBeforeUnload();
  });
}

function cleanupConnectionBeforeUnload(): void {
  if (cleanupComplete) return;

  try {
    const playerId = getLocalPlayerId();
    if (playerId && socket && socket.readyState === WebSocket.OPEN) {
      // Send a final message to tell the server we're disconnecting
      socket.send(
        JSON.stringify({
          type: "disconnect",
          payload: { playerId },
        })
      );
    }

    // Clean up resources
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }

    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    cleanupComplete = true;
  } catch (e) {
    console.error("Error during connection cleanup:", e);
  }
}

export function initializeSocket(): void {
  // Clean up any existing connection
  if (socket) {
    try {
      socket.close();
    } catch (e) {
      console.error("Error closing existing socket:", e);
    }
  }

  manuallyDisconnected = false;
  reconnectAttempt = 0;
  cleanupComplete = false;
  disconnectedSince = null;

  // Setup unload handlers (only once)
  setupPageUnloadHandlers();

  connectToServer();
}

function connectToServer(): void {
  try {
    console.log(`Attempting to connect to WebSocket server at: ${serverUrl}`);
    socket = new WebSocket(serverUrl);

    socket.onopen = () => {
      console.log(
        `[${new Date().toISOString()}] WebSocket connected to`,
        serverUrl
      );
      reconnectAttempt = 0;
      hideReconnectingOverlay();

      // Start sending heartbeats
      if (heartbeatInterval) {
        clearInterval(heartbeatInterval);
      }

      heartbeatInterval = window.setInterval(() => {
        if (socket && socket.readyState === WebSocket.OPEN) {
          // Record timestamp for latency calculation
          latencyStats.lastPing = Date.now();
          sendMessage("heartbeat");
        }
      }, 1000); // Measure more frequently (1 second)
    };

    socket.onmessage = handleSocketMessage;

    socket.onclose = () => {
      console.log("WebSocket connection closed");
      handleDisconnection();
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      handleDisconnection();
    };
  } catch (error) {
    console.error("Failed to create WebSocket connection:", error);
    handleDisconnection();
  }
}

function handleDisconnection(): void {
  // Clean up the heartbeat interval
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }

  // Track when the disconnection happened
  if (!disconnectedSince) {
    disconnectedSince = Date.now();
  }

  // If we manually closed the connection, don't attempt to reconnect
  if (manuallyDisconnected) {
    return;
  }

  // Show the reconnecting overlay with current attempt information
  showReconnectingOverlay(reconnectAttempt, MAX_RECONNECT_ATTEMPTS);

  // Calculate exponential backoff delay
  const delay = calculateBackoffDelay(reconnectAttempt, BASE_RECONNECT_DELAY);

  if (reconnectAttempt < MAX_RECONNECT_ATTEMPTS) {
    console.log(
      `Attempting to reconnect in ${delay}ms (attempt ${
        reconnectAttempt + 1
      }/${MAX_RECONNECT_ATTEMPTS})`
    );

    // Schedule reconnection
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
    }

    reconnectTimeout = window.setTimeout(() => {
      reconnectAttempt++;
      connectToServer();
    }, delay);
  } else {
    console.log(
      "Maximum reconnection attempts reached. Please refresh the page."
    );
    showReconnectingOverlay(
      reconnectAttempt,
      MAX_RECONNECT_ATTEMPTS,
      true, // maxAttemptsReached
      () => {
        // Reset everything and try one more time when user clicks the button
        reconnectAttempt = 0;
        disconnectedSince = null;
        connectToServer();
      }
    );
  }
}

// Adaptive batching based on game activity
function updateBatchInterval(): void {
  const now = Date.now();
  const timeSinceActivity = now - lastActivity;

  // If we've seen recent activity, use faster interval
  if (timeSinceActivity < 100) {
    currentBatchInterval = MIN_BATCH_INTERVAL;
  }
  // Otherwise gradually increase interval for efficiency
  else if (
    timeSinceActivity > 1000 &&
    currentBatchInterval < MAX_BATCH_INTERVAL
  ) {
    currentBatchInterval = Math.min(
      currentBatchInterval * 1.5,
      MAX_BATCH_INTERVAL
    );
  }

  // Update batch timer with new interval if needed
  if (batchSendInterval !== null) {
    window.clearInterval(batchSendInterval);
    batchSendInterval = window.setInterval(
      flushMessageBatch,
      currentBatchInterval
    );
  }
}

// Send message with optimized batching
export function sendMessage<T extends object = Record<string, unknown>>(
  type: MessageType | string,
  payload: T = {} as T
): void {
  const message: IMessage = {
    type: type as MessageType,
    payload,
  };

  // Update activity timestamp for adaptive batching
  lastActivity = Date.now();

  // Special case for urgent messages (heartbeat, disconnect)
  // These should be sent immediately
  if (type === "heartbeat" || type === "disconnect") {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
    }
    return;
  }

  // For move messages, replace any pending move messages rather than append
  if (type === "move" && pendingMessages.length > 0) {
    const existingMoveIndex = pendingMessages.findIndex(
      (msg) => msg.type === "move"
    );
    if (existingMoveIndex >= 0) {
      pendingMessages[existingMoveIndex] = message;
      return;
    }
  }

  // Add message to batch
  pendingMessages.push(message);

  // Start batch interval if not already running
  if (batchSendInterval === null) {
    updateBatchInterval();
    batchSendInterval = window.setInterval(
      flushMessageBatch,
      currentBatchInterval
    );
  }
}

// Send all pending messages as a batch
function flushMessageBatch(): void {
  if (pendingMessages.length === 0) {
    // No messages to send, clear interval to save resources
    if (batchSendInterval !== null) {
      window.clearInterval(batchSendInterval);
      batchSendInterval = null;
    }
    return;
  }

  if (socket && socket.readyState === WebSocket.OPEN) {
    // If only one message, send it directly
    if (pendingMessages.length === 1) {
      socket.send(JSON.stringify(pendingMessages[0]));
    } else {
      // Multiple messages: send as batch in a single network request
      const batchMessage: IMessage = {
        type: "batch",
        payload: { messages: pendingMessages },
      };
      socket.send(JSON.stringify(batchMessage));
    }

    // Clear pending messages
    pendingMessages.length = 0;
  }
}

function handleSocketMessage(event: MessageEvent): void {
  try {
    const message = JSON.parse(event.data) as IMessage;

    // Handle batch messages if they come from server
    if (
      message.type === "batch" &&
      message.payload &&
      typeof message.payload === "object" &&
      "messages" in message.payload &&
      Array.isArray((message.payload as BatchPayload).messages)
    ) {
      for (const msg of (message.payload as BatchPayload).messages) {
        processMessage(msg);
      }
      return;
    }

    // Process individual message
    processMessage(message);
  } catch (err) {
    console.error("Failed to parse server message", err);
  }
}

// Process individual message
function processMessage(message: IMessage): void {
  switch (message.type) {
    case "welcome":
      messageHandlers.handleWelcomeMessage(message, disconnectedSince);
      disconnectedSince = null;
      break;
    case "join":
      messageHandlers.handleJoinMessage(message);
      break;
    case "leave":
      messageHandlers.handleLeaveMessage(message);
      break;
    case "move":
      messageHandlers.handleMoveMessage(message);
      break;
    case "ghostMove":
      messageHandlers.handleGhostMoveMessage(message);
      break;
    case "collision":
      messageHandlers.handleCollisionMessage(message);
      break;
    case "respawn":
      messageHandlers.handleRespawnMessage(message);
      break;
    case "collect":
      messageHandlers.handleCollectMessage(message);
      break;
    case "rocket":
      messageHandlers.handleRocketMessage(message);
      break;
    case "echo":
      messageHandlers.handleEchoMessage(message);
      break;
    case "roundEnd":
      messageHandlers.handleRoundEndMessage(message);
      break;
    case "heartbeat":
      // Calculate round trip time
      if (latencyStats.lastPing > 0) {
        const latency = Date.now() - latencyStats.lastPing;
        latencyStats.update(latency);
        updateLatencyDisplay();
      }
      break;
  }
}

export function disconnect(): void {
  manuallyDisconnected = true;

  cleanupConnectionBeforeUnload();

  if (socket) {
    socket.close();
    socket = null;
  }
}

// Add connection quality tracking to adjust game predictions
export const connectionStats = {
  lastLatencies: [] as number[],
  averageLatency: 0,
  jitter: 0,
  packetLoss: 0,
  qualityFactor: 1.0, // Factor to adjust prediction confidence
  samples: 0,

  // Update connection stats with new latency value
  update(latency: number): void {
    // Ignore extreme outliers that might skew averages
    if (this.samples > 5 && latency > this.averageLatency * 5) {
      return;
    }

    this.samples++;
    this.lastLatencies.push(latency);

    // Keep only the last 20 measurements for better averaging
    if (this.lastLatencies.length > 20) {
      this.lastLatencies.shift();
    }

    // Calculate average latency (with outlier removal)
    if (this.lastLatencies.length >= 5) {
      // Sort latencies to find median and percentiles
      const sorted = [...this.lastLatencies].sort((a, b) => a - b);

      // Use 75th percentile as threshold for outlier removal
      const p75 = sorted[Math.floor(sorted.length * 0.75)];
      const threshold = p75 * 2;

      // Filter out high outliers for averaging
      const filtered = sorted.filter((val) => val <= threshold);
      const sum = filtered.reduce((a, b) => a + b, 0);
      this.averageLatency = sum / filtered.length;
    } else {
      // Simple average for few samples
      const sum = this.lastLatencies.reduce((a, b) => a + b, 0);
      this.averageLatency = sum / this.lastLatencies.length;
    }

    // Calculate jitter (variation in latency)
    if (this.lastLatencies.length >= 3) {
      let totalVariation = 0;
      let validPairs = 0;

      for (let i = 1; i < this.lastLatencies.length; i++) {
        const variation = Math.abs(
          this.lastLatencies[i] - this.lastLatencies[i - 1]
        );
        if (variation < this.averageLatency * 2) {
          totalVariation += variation;
          validPairs++;
        }
      }

      this.jitter = validPairs > 0 ? totalVariation / validPairs : 0;
    }

    // Update quality factor (used for client prediction)
    // Higher latency and jitter = less confidence in predictions
    const latencyFactor = Math.min(1, 50 / this.averageLatency);
    const jitterFactor = Math.min(1, 20 / this.jitter);

    // Combined quality from 0.1 (bad) to 1.0 (perfect)
    this.qualityFactor = Math.max(
      0.1,
      latencyFactor * 0.7 + jitterFactor * 0.3
    );

    // Update batch interval based on connection quality
    updateBatchInterval();
  },

  // Get connection quality rating
  getQuality(): "excellent" | "good" | "fair" | "poor" {
    if (this.qualityFactor > 0.8) return "excellent";
    if (this.qualityFactor > 0.6) return "good";
    if (this.qualityFactor > 0.3) return "fair";
    return "poor";
  },
};
